import {
  type MotionPreference,
  MOTION_STORAGE_KEY,
  readOsReducedMotion,
  resolveReducedMotion,
} from '@/lib/motion';

function getCachedPreference(): MotionPreference {
  if (typeof window === 'undefined') return 'system';
  const cached = localStorage.getItem(MOTION_STORAGE_KEY);
  if (cached === 'full' || cached === 'reduced' || cached === 'system') {
    return cached;
  }
  return 'system';
}

/** Apply data-reduce-motion on <html> before first paint and on changes. */
export function applyReducedMotionAttribute(reduced: boolean) {
  document.documentElement.dataset.reduceMotion = reduced ? 'true' : 'false';
}

export function computeEffectiveReducedMotion(
  preference: MotionPreference = getCachedPreference(),
): boolean {
  return resolveReducedMotion(preference, readOsReducedMotion());
}

/** Call synchronously before React render to avoid motion flash. */
export function initMotionBootstrap() {
  applyReducedMotionAttribute(computeEffectiveReducedMotion());
}

/** Keep attribute in sync with OS preference and storage changes. */
export function startMotionBootstrapListener() {
  const update = () => {
    applyReducedMotionAttribute(computeEffectiveReducedMotion());
  };

  const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
  mq.addEventListener('change', update);
  window.addEventListener('motion-preference-change', update);
  window.addEventListener('storage', (e) => {
    if (e.key === MOTION_STORAGE_KEY) update();
  });

  return () => {
    mq.removeEventListener('change', update);
    window.removeEventListener('motion-preference-change', update);
  };
}

/** Persist preference to localStorage for bootstrap (used by useMotionPreference in Phase 6). */
export function cacheMotionPreference(preference: MotionPreference) {
  localStorage.setItem(MOTION_STORAGE_KEY, preference);
  applyReducedMotionAttribute(resolveReducedMotion(preference, readOsReducedMotion()));
  window.dispatchEvent(new Event('motion-preference-change'));
}
